import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const SafariZoneTimer = () => {
  const [remainingTime, setRemainingTime] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const navigate = useNavigate();

  // Format seconds into MM:SS format
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Check Safari Zone access status
  const checkAccess = useCallback(async () => {
    try {
      const response = await api.get('/v1/safari-zone/status', { withCredentials: true });
      
      if (response.data && response.data.has_access) {
        setRemainingTime(response.data.remaining_seconds);
      } else {
        setIsExpired(true);
        // Redirect to entrance after a short delay if access has expired
        setTimeout(() => {
          navigate('/map/SafariZone');
        }, 1000);
      }
    } catch (error) {
      console.error('Error checking Safari Zone access:', error);
      setIsExpired(true);
    }
  }, [navigate]);

  // Initialize and set up timer
  useEffect(() => {
    checkAccess();
    
    // Set up interval to update timer every second
    const timerInterval = setInterval(() => {
      setRemainingTime(prevTime => {
        if (prevTime === null || prevTime === undefined) return null;
        
        // If time is up, set expired and clear interval
        if (prevTime <= 1) {
          setIsExpired(true);
          clearInterval(timerInterval);
          
          // Show expiration message and redirect
          const textElement = document.createElement('div');
          textElement.innerText = "Safari Zone time expired!";
          textElement.style.position = 'absolute';
          textElement.style.left = '50%';
          textElement.style.top = '50%';
          textElement.style.transform = 'translate(-50%, -50%)';
          textElement.style.color = '#ff3333';
          textElement.style.fontSize = '24px';
          textElement.style.fontWeight = 'bold';
          textElement.style.textShadow = '2px 2px 4px rgba(0, 0, 0, 0.7)';
          textElement.style.zIndex = 2000;
          textElement.style.padding = '15px 25px';
          textElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          textElement.style.borderRadius = '8px';
          textElement.style.animation = 'fadeIn 0.5s forwards, fadeOut 0.5s forwards 2.5s';
          
          // Add the element to the DOM
          document.body.appendChild(textElement);
          
          // Remove the message after 3 seconds
          setTimeout(() => {
            if (textElement && textElement.parentNode) {
              textElement.parentNode.removeChild(textElement);
            }
          }, 3000);
          
          // Add fade-in animation if it doesn't exist
          if (!document.getElementById('safari-timer-style')) {
            const style = document.createElement('style');
            style.id = 'safari-timer-style';
            style.innerHTML = `
              @keyframes fadeIn {
                0% { opacity: 0; }
                100% { opacity: 1; }
              }
              @keyframes fadeOut {
                0% { opacity: 1; }
                100% { opacity: 0; }
              }
            `;
            document.head.appendChild(style);
          }
          
          // Redirect after showing message
          setTimeout(() => {
            navigate('/map/SafariZone');
          }, 2000);
          
          return 0;
        }
        
        return prevTime - 1;
      });
    }, 1000);
    
    // Check access status every 30 seconds as a backup
    const accessCheckInterval = setInterval(checkAccess, 30000);
    
    return () => {
      clearInterval(timerInterval);
      clearInterval(accessCheckInterval);
      
      // Clean up any style elements and expiration message
      const styleElement = document.getElementById('safari-timer-style');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
      
      // Remove any lingering expiration messages
      const expiredMessages = document.querySelectorAll('div');
      expiredMessages.forEach(el => {
        if (el.innerText === "Safari Zone time expired!") {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, [navigate, checkAccess]);

  // Don't render anything if we don't have access
  if (isExpired) return null;

  return (
    <div className="safari-zone-timer">
      <div className="timer-container">
        <div className="timer-label">Safari Zone Time:</div>
        <div className="timer-value">{formatTime(remainingTime)}</div>
      </div>
      <style jsx="true">{`
        .safari-zone-timer {
          position: fixed;
          top: 80px;
          right: 10px;
          z-index: 1000;
          pointer-events: none;
        }
        
        .timer-container {
          background: linear-gradient(to bottom, #4a6c2f, #2c4018);
          border: 2px solid #8bc34a;
          border-radius: 8px;
          padding: 8px 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .timer-label {
          color: #d4e157;
          font-size: 12px;
          font-weight: bold;
          margin-bottom: 2px;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
        }
        
        .timer-value {
          color: #ffffff;
          font-size: 18px;
          font-weight: bold;
          font-family: monospace;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
        }
      `}</style>
    </div>
  );
};

export default SafariZoneTimer;
